import merge from 'lodash/merge';

import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {createSelector} from 'reselect';
import {RootState} from '../../types';
import {UIState} from './types';
import {EHeaderType} from './types';

const uiSelector = (state: RootState) => state.ui;
const openedFlyoutIdsSelector = (state: RootState) => state.ui.openedFlyoutIds;
const openedPopupIdsSelector = (state: RootState) => state.ui.openedPopupIds;
const navigationStackSelector = (state: RootState) => state.ui.navigationStack;
const spinnerStatusSelector = (state: RootState) => state.ui.spinner;
const headerSelector = (state: RootState) => state.ui.header;
const requestClassesSelector = (state: RootState) => state.ui.requestClasses;

//TODO: remove a callback from sizeSelector when event based approach will be done
export const uiSlice = createSlice({
	name: 'ui',
	initialState: {
		flyout: undefined,
		sizeSelector: {
			variantID: undefined,
			productID: undefined,
			countryCode: undefined
		},
		sizeSelectorSelectedPID: undefined,
		colorSelector: {
			variantID: undefined,
			productID: undefined
		},
		colorSelectorSelectedPID: undefined,
		notifyMe: {
			productID: undefined,
			selectedVariationID: undefined,
		},
		pdpZoom: {
			isActive: false,
			imageIndex: 0,
			clickedCoordinates: {x: undefined, y: undefined},
			isSecondaryZoom: false,
		},
		countryConfirmRedirectObj: {},
		openedPopupIds: [],
		openedFlyoutIds: [],
		spinner: {
			spinner: false
		},
		isFooterClick: false,
		rotationalBanner: true,
		header: {
			show: true,
			preventHeaderFromHiding: false,
			locked: false,
			type: EHeaderType.DEFAULT,
			isNavigationColorWhite: false,
			isNavigationOpened: false,
			itemsToHide: [],
		},
		footer: {
			show: true,
		},
		navigationStack: {},
		navigatingToCorrectLocale: false,
		myAccount: {
			isLoading: false,
			hasReferAFriend: undefined
		},
		window: {
			viewportHeight: undefined,
		},
		htmlCssClasses: [],
		mainContainerCssClasses: [],
		ssrGlobalStyles: {},
		requestClasses: [],
		isWishlistClick: false,
		filtersOpened: {},
		loginFlyout: {
			titleKey: 'defaultTitle'
		},
		sizeGuide: {
			sizeGuideId: undefined,
			itemMeasurements: undefined,
			onClose: undefined,
			tabType: undefined,
			productID: undefined,
		},
		searchConfig: {
			searchType: undefined,
			searchInstanceKey: undefined,
		},
		backToTop: true,
		scrollAfterLoading: true,
		isServer: true,
		disableAddToCart: false,
		loginTabKey: 'login',
		pageViewCounter: 0,
		previousUrl: '',
		editAddressId: null,
		storeDetail: {
			storeId: undefined
		},
		matchItWith: {
			variationGroupId: '',
			selectedProductId: '',
		},
		personalizationFlyout: {
			variationGroupID: '',
			variantID: '',
			message: '',
			basketItemId: '',
		},
		badges: false,
		requiresLoginFeature: {
			enabled: false
		}
	} as unknown as UIState,
	reducers: {
		receiveUI: (state: UIState, action: PayloadAction<any>) => {
			return merge(state, action.payload);
		},
		receiveReplaceUI: (state: UIState, action: PayloadAction<any>) => {
			return Object.assign(state, action.payload);
		},
		receiveOpenPopups: (state: UIState, action: PayloadAction<any>) => {
			state.openedPopupIds = action.payload;
		},
		receiveOpenFlyouts: (state: UIState, action: PayloadAction<string[]>) => {
			state.openedFlyoutIds = action.payload;
		},
		receiveNavigationStack: (state: UIState, action: PayloadAction<any>) => {
			state.navigationStack = action.payload || {};
		},
		receiveSpinnerStatus: (state: UIState, action: PayloadAction<any>) => {
			state = merge(state, {
				spinner: action.payload,
			});
		},
		receiveHeader: (state: UIState, action: PayloadAction<any>) => {
			state.header = {...state.header, ...action.payload};
		},
		receiveCountryConfirmRedirectObj: (state: UIState, action: PayloadAction<any>) => {
			state.countryConfirmRedirectObj = {...state.countryConfirmRedirectObj, ...action.payload};
		},
		receivePageViewCounter: (state: UIState) => {
			state.pageViewCounter += 1;
		},
		receivePreviousUrl: (state: UIState, action: PayloadAction<string>) => {
			state.previousUrl = action.payload;
		},
		receiveSsrGlobalStyle: (state: UIState, action: PayloadAction<{key: string; value: string; isSsr: boolean}>) => {
			const {key, value, isSsr} = action.payload;

			if (!key || !value) {
				return;
			}

			const entry = state.ssrGlobalStyles[key];

			if (!entry) {
				state.ssrGlobalStyles[key] = {style: value, usages: 1, ssrUsages: isSsr ? 1 : 0};

				return;
			}

			// A client mount hydrating a rule already counted on the server claims that usage
			// instead of counting it twice, regardless of when its Suspense boundary hydrates.
			if (!isSsr && entry.ssrUsages > 0) {
				entry.ssrUsages -= 1;

				return;
			}

			entry.usages += 1;

			if (isSsr) {
				entry.ssrUsages += 1;
			}
		},
		removeSsrGlobalStyle: (state: UIState, action: PayloadAction<string>) => {
			const entry = state.ssrGlobalStyles[action.payload];

			if (!entry) {
				return;
			}

			entry.usages -= 1;

			if (entry.usages <= 0) {
				delete state.ssrGlobalStyles[action.payload];
			}
		},
		clearSsrGlobalStyles: (state: UIState) => {
			state.ssrGlobalStyles = {};
		},
	},
});

export const {
	receiveOpenFlyouts,
	receiveReplaceUI,
	receiveUI,
	receiveSpinnerStatus,
	receiveOpenPopups,
	receiveNavigationStack,
	receiveHeader,
	receiveCountryConfirmRedirectObj,
	receivePageViewCounter,
	receivePreviousUrl,
	receiveSsrGlobalStyle,
	removeSsrGlobalStyle,
	clearSsrGlobalStyles,
} = uiSlice.actions;

export const selectUI = createSelector(uiSelector, (ui) => ui);
export const selectOpenedFlyoutIds = createSelector(openedFlyoutIdsSelector, (openedFlyoutIds) => openedFlyoutIds);
export const selectSpinnerStatus = createSelector(spinnerStatusSelector, (spinner) => spinner);
export const selectOpenedPopupIds = createSelector(openedPopupIdsSelector, (openedPopupIds) => openedPopupIds);
export const selectNavigationStack = createSelector(navigationStackSelector, (navigationStack) => navigationStack);
export const selectRequestClasses = createSelector(requestClassesSelector, (requestClasses) => requestClasses);

const selectID = (state, elementID) => elementID;

/**
 * Returns the whole three impacting the given ID keeping the original order
 */
export const selectNavigationStackTree = createSelector(
	[navigationStackSelector, selectID],
	(navigationStack, elementID) => {
		const tmp = {};
		const res = {};
		let currentSearchingID = elementID;

		while (currentSearchingID in navigationStack) {
			tmp[currentSearchingID] = navigationStack[currentSearchingID];
			currentSearchingID = navigationStack[currentSearchingID];
		}

		currentSearchingID = elementID;

		while (currentSearchingID) {
			const idToSearch = currentSearchingID;
			currentSearchingID = null;

			Object.keys(navigationStack).forEach((i) => {
				const v = navigationStack[i];

				if (v === idToSearch && !(i in tmp)) {
					tmp[i] = v;
					currentSearchingID = i;

					return;
				}
			});
		}

		Object.keys(navigationStack).forEach((k) => {
			if (tmp[k]) {
				res[k] = tmp[k];
			}
		});

		return res;
	},
);

const selectPopupID = (state, popupID) => popupID;
export const selectPopupIndex = createSelector([selectOpenedPopupIds, selectPopupID], (openedPopupIds, popupID) =>
	openedPopupIds.indexOf(popupID),
);
export const selectHeaderUI = createSelector(headerSelector, (header) => header);

export default uiSlice.reducer;
